import type { CSSProperties } from 'react';
import type { RhythmPattern } from '../utils/rhythmPattern';
import { globalCellIndex } from '../utils/pattern';
import { rhythmToMelodyPattern } from '../utils/rhythmPlayback';

interface Props {
  pattern: RhythmPattern;
  activeIndex: number | null;
  playing: boolean;
  accentColor: string;
  discoverLayout?: boolean;
}

/** Paleta vibrante tipo app infantil, un color por tiempo */
const BEAT_COLORS = [
  '#FF5252',
  '#FF9D00',
  '#22C55E',
  '#2DA8F5',
  '#A855F7',
  '#EC4899',
  '#14B8A6',
];

/** Guía visual al escuchar: boxes que laten sin revelar figuras */
export function RhythmPulseGrid({
  pattern,
  activeIndex,
  playing,
  accentColor,
  discoverLayout = false,
}: Props) {
  const melodyView = rhythmToMelodyPattern(pattern);

  const measuresContent = pattern.measures.map((measure, mi) => (
    <section
      key={mi}
      className="measure-block measure-block--pulse"
      aria-label={`Compás ${mi + 1}`}
    >
      <div className="measure-label">Compás {mi + 1}</div>
      <div className="measure-beats">
        {measure.slots.map((slot, bi) => {
          if (!slot.active) return null;

          const spanClass = slot.span > 1 ? `beat-slot--span-${slot.span}` : '';
          const count = slot.cells.length;
          const color = BEAT_COLORS[bi % BEAT_COLORS.length];

          const firstG = globalCellIndex(mi, bi, 0, melodyView);
          const beatActive =
            playing &&
            activeIndex !== null &&
            activeIndex >= firstG &&
            activeIndex < firstG + count;

          return (
            <div
              key={bi}
              className={`beat-slot beat-slot--pulse ${spanClass} ${beatActive ? 'beat-slot--beating' : ''}`}
              style={
                {
                  flex: slot.span,
                  '--beat-color': color,
                } as CSSProperties
              }
            >
              <div className="beat-box__top">
                <span className="beat-box__tag">Tiempo {bi + 1}</span>
              </div>

              <div className="beat-box__mystery" aria-hidden="true">
                ?
              </div>

              <div className={`beat-box__pulses beat-box__pulses--n${count}`}>
                {slot.cells.map((cell, ci) => {
                  const gIdx = globalCellIndex(mi, bi, ci, melodyView);
                  const isActive = playing && activeIndex === gIdx;
                  return (
                    <span
                      key={ci}
                      className={`pulse-dot ${cell.hit ? 'pulse-dot--hit' : 'pulse-dot--rest'} ${isActive ? 'pulse-dot--active' : ''}`}
                      aria-hidden="true"
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  ));

  return (
    <div
      className={`pattern-grid pattern-grid--pulse ${discoverLayout ? 'pattern-grid--discover-fit' : ''}`}
      style={{ '--accent': accentColor } as CSSProperties}
    >
      {discoverLayout ? (
        <div className="discover-measures-grid" data-count={pattern.measures.length}>
          {measuresContent}
        </div>
      ) : (
        measuresContent
      )}
    </div>
  );
}
